import { FC } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { alpha } from "@mui/material/styles";
import { buildCloudinarySquareUrl, getYoutubeIdentifier } from "@simplyk/common";

import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ExternalLink } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

const YOUTUBE_THUMBNAIL_QUALITY = "hqdefault";

interface CampaignCardMediaProps {
  bannerUrl: string | null;
  bannerVideoUrl: string | null;
  categoryIcon: IconElement;
  hoverLabel: string;
}

export const CampaignCardMedia: FC<CampaignCardMediaProps> = ({
  bannerUrl,
  bannerVideoUrl,
  categoryIcon,
  hoverLabel,
}) => {
  const imageUrl = getBannerImageUrl(bannerUrl, bannerVideoUrl);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden", borderRadius: "inherit" }}>
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt=""
          loading="lazy"
          sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={(theme) => ({
            width: "100%",
            height: "100%",
            backgroundColor: alpha(theme.palette.forms.text.intense, 0.1),
          })}
        >
          <FormIcon
            size="other"
            sx={(theme) => ({
              width: theme.spacing(5),
              height: theme.spacing(5),
              color: theme.palette.forms.text.intense,
              opacity: 0.4,
            })}
          >
            {categoryIcon}
          </FormIcon>
        </Stack>
      )}
      <Stack
        data-role="campaign-card-hover-overlay"
        alignItems="center"
        justifyContent="center"
        gap={0.5}
        sx={(theme) => ({
          position: "absolute",
          inset: 0,
          opacity: 0,
          transition: "opacity 200ms ease-out",
          backgroundColor: alpha(theme.palette.forms.surface.intense, 0.7),
          color: theme.palette.common.white,
          textAlign: "center",
          paddingInline: 1,
        })}
      >
        <FormIcon size="medium" sx={{ color: "inherit" }}>
          <ExternalLink />
        </FormIcon>
        <FormTypography variant="subtitle2" sx={(theme) => ({ color: "inherit", lineHeight: theme.spacing(3.5) })}>
          {hoverLabel}
        </FormTypography>
      </Stack>
    </Box>
  );
};

const getBannerImageUrl = (bannerUrl: string | null, bannerVideoUrl: string | null): string | null => {
  if (bannerUrl) {
    return buildCloudinarySquareUrl(bannerUrl);
  }
  const youtubeVideoId = bannerVideoUrl ? getYoutubeIdentifier(bannerVideoUrl) : undefined;
  return youtubeVideoId ? `https://img.youtube.com/vi/${youtubeVideoId}/${YOUTUBE_THUMBNAIL_QUALITY}.jpg` : null;
};
